// Chamber Set Up > Provincial Chamber Of Commerce Fee > "Show separate on
// invoice". Unchecked, invoice views show the fee inside the membership line,
// the same as the backend PDF (cmpInvoiceProvincialFeeDisplay.ts). Display
// only: the saved invoice keeps its own provincial line.

const MEMBERSHIP_BASE_FEE_LINE_ITEM_ID = "membership-base-fee";
const MEMBERSHIP_PROVINCIAL_FEE_LINE_ITEM_ID =
  "membership-provincial-chamber-fee";

type DisplayLineItem = {
  id: string;
  quantity: number;
  unitPriceInCents: number;
  subtotalAmountInCents: number;
  taxAmountInCents: number;
  totalAmountInCents: number;
};

export const foldProvincialChamberFeeForDisplay = <
  LineItem extends DisplayLineItem,
>({
  lineItems,
  showSeparately,
}: {
  lineItems: readonly LineItem[];
  /** Unknown (still loading) keeps the saved lines. */
  showSeparately?: boolean | null;
}): readonly LineItem[] => {
  if (showSeparately !== false) {
    return lineItems;
  }

  const baseLine = lineItems.find(
    (lineItem) => lineItem.id === MEMBERSHIP_BASE_FEE_LINE_ITEM_ID,
  );
  const provincialLines = lineItems.filter(
    (lineItem) => lineItem.id === MEMBERSHIP_PROVINCIAL_FEE_LINE_ITEM_ID,
  );

  if (!baseLine || provincialLines.length === 0) {
    return lineItems;
  }

  const sum = (field: keyof Omit<DisplayLineItem, "id">) =>
    provincialLines.reduce(
      (total, lineItem) => total + (Number(lineItem[field]) || 0),
      0,
    );
  const quantity = baseLine.quantity > 0 ? baseLine.quantity : 1;
  const subtotalAmountInCents =
    baseLine.subtotalAmountInCents + sum("subtotalAmountInCents");
  const foldedBaseLine: LineItem = {
    ...baseLine,
    unitPriceInCents: Math.round(subtotalAmountInCents / quantity),
    subtotalAmountInCents,
    taxAmountInCents: baseLine.taxAmountInCents + sum("taxAmountInCents"),
    totalAmountInCents: baseLine.totalAmountInCents + sum("totalAmountInCents"),
  };

  return lineItems
    .filter((lineItem) => lineItem.id !== MEMBERSHIP_PROVINCIAL_FEE_LINE_ITEM_ID)
    .map((lineItem) => (lineItem === baseLine ? foldedBaseLine : lineItem));
};
